'use client';

import { memo } from 'react';
import Counter from '@/_components/Counter';
import NumberToPrice from '@/_components/NumberToPrice';
import { AspectRatio, Box, Card, Flex, Stack, Text } from '@chakra-ui/react';
// import defaultImage from '@/_assets/illustration/no_photo.svg'
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import {
  AddIcon,
  CheckboxIcon,
  QuestionIcon,
} from '@/_assets/icon-components';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

interface IProps {
  data: any;
  inStop: boolean;
  checked: boolean;
  quantity: number;
  isFree?: boolean;
  label: string;
  outPrice: number;
  isCheckbox: boolean;
  increase: () => void;
  decrease: () => void;
  isCompulsory?: boolean;
  onChange: (e: any) => void;
}

function ModifierCheckbox({
  data,
  checked,
  quantity,
  isFree = false,
  isCheckbox = false,
  inStop = false,
  label,
  outPrice,
  increase,
  decrease,
  isCompulsory = false,
  onChange,
}: IProps) {
  const t = useTranslations();

  if (isCheckbox) {
    return (
      <Card.Root
        p={0}
        rounded={16}
        border='none'
        bg={'light.300'}
        cursor='pointer'
        opacity={inStop ? 0.5 : 1}
      >
        <Card.Header p={2} borderRadius={'12px'}>
          <AspectRatio ratio={1 / 1} maxH={'142px'} rounded={12}>
            {data?.image ? (
              <Image
                src={process.env.BASE_URL + data?.image}
                alt='modifier'
                style={{
                  borderRadius: '12px',
                }}
                fill
              />
            ) : (
              <Box>
                <QuestionIcon />
              </Box>
            )}
          </AspectRatio>
        </Card.Header>
        <Card.Body p={2} py={0}>
          <Text fontSize='sm'>{label}</Text>
        </Card.Body>
        <Card.Footer p={2} pb='10px' justifyContent='center'>
          {inStop ? (
            <Text textAlign={'center'}>{t('unavailable')}</Text>
          ) : quantity > 0 ? (
            <Counter
              height='40px'
              variable={quantity}
              onIncrease={increase}
              onDecrease={decrease}
            />
          ) : (
            <Button
              color='black'
              display={'flex'}
              justifyContent={'space-between'}
              w={'full'}
              size={'md'}
              rounded={12}
              bg={'light'}
              _hover={{
                bgColor: 'light.200',
              }}
              onClick={increase}
            >
              {isFree ? (
                t('free')
              ) : (
                <NumberToPrice value={outPrice ? outPrice : 0} />
              )}
              <AddIcon />
            </Button>
          )}
        </Card.Footer>
      </Card.Root>
    );
  }

  return (
    <Flex
      py={3}
      px={3}
      gap={4}
      h={'60px'}
      rounded={8}
      bg={'gray.400'}
      cursor='pointer'
      alignItems={'center'}
      opacity={inStop ? 0.5 : 1}
      justifyContent={'space-between'}
      borderColor={inStop ? '#f008' : checked ? 'primary.400' : '#f1f1f1'}
    >
      <Flex
        alignItems={'center'}
        w={'100%'}
        gap={4}
        onClick={() => {
          if (!inStop) onChange({ target: { checked: !checked } });
        }}
      >
        <Checkbox
          id='custom-checkbox'
          icon={
            checked ? (
              <CheckboxIcon color='var(--primary-default)' />
            ) : (
              <>
                {data?.image ? (
                  <Image
                    src={process.env.BASE_URL + data?.image}
                    alt='modifier'
                    width={100}
                    height={100}
                    style={{
                      borderRadius: '50%',
                      objectFit: 'cover',
                      width: '28px',
                      height: '28px',
                    }}
                  />
                ) : (
                  <Box w={7} h={7} rounded='full' bg='light' />
                )}
              </>
            )
          }
          checked={checked}
          size='lg'
          rounded='full'
          unstyled
        />
        <Stack gap={'2px'} fontSize='sm'>
          <Text as='span' color={checked ? 'gray.600' : 'text'}>
            {label}
          </Text>
          <Text
            as='span'
            color='#43A250'
            fontWeight={500}
            fontSize='sm'
            display='block'
          >
            {inStop ? (
              <Text
                fontSize='sm'
                as='span'
                color='red'
                fontWeight={500}
                display='block'
              >
                {t('unavailable')}
              </Text>
            ) : isFree || outPrice === 0 ? (
              t('free')
            ) : (
              <Text color={checked ? 'text' : 'gray.600'}>
                <NumberToPrice
                  value={outPrice || 0}
                  textProps={{ as: 'span' }}
                />
              </Text>
            )}
          </Text>
        </Stack>
      </Flex>

      <Box>
        {inStop ? (
          <Text w={'100px'}>{t('unavailable')}</Text>
        ) : (
          quantity > 0 && (
            <Counter
              width={'100px'}
              height='32px'
              variable={quantity}
              onIncrease={increase}
              onDecrease={decrease}
            />
          )
        )}
      </Box>
    </Flex>
  );
}
export default memo(ModifierCheckbox);
